import React from "react";

import { STATS_TEXT as S } from "./catalogStatsText";
import { causeShares, diagnosisTiles, ruledOutCauses, timelineSteps } from "./catalogStats";
import DiagnosisTimeline from "./DiagnosisTimeline";
import { RoughHead, ShareRow, SkeletonGrid, StatGrid } from "./StatTiles";
import { CONTEXT_STATUS } from "./useSessionContext";

/** Where each cause stood at the end, the learned one marked; the causes beyond the server's cap are counted. */
function Shares({ diagnosis }) {
  const shares = causeShares(diagnosis);
  if (!shares.length) return <p className="le-muted">{S.noCauses}</p>;
  const more = Math.max(0, (diagnosis.causes_considered || 0) - shares.length);
  return (
    <div className="le-control">
      <ul className="le-shares" aria-label={S.causes.heading} title={S.causes.how}>
        {shares.map((share) => <ShareRow key={share.key} share={share} />)}
      </ul>
      {more > 0 && <p className="le-muted">{S.causes.more(more)}</p>}
    </div>
  );
}

function RuledOut({ diagnosis }) {
  const causes = ruledOutCauses(diagnosis);
  if (!causes.length) return null;
  return (
    <div className="le-control">
      <span className="le-label">{S.ruledOutHeading}</span>
      <ul className="le-plain-list">
        {causes.map((c) => (
          <li key={c.key} className="le-muted">
            <span className="le-strike">{c.name}</span>{c.reason ? ` · ${c.reason}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "How the diagnosis went": the figures of the diagnosis a learned case or pattern came from (the `diagnosis` block of the
 * session context), as compact tiles with meters, then where each cause stood, what was ruled out, the fix and the timeline.
 * Every figure is labelled rough. `context` is the useSessionContext result; while it loads the tiles and the timeline show
 * placeholders with the final geometry; a conversation without a stored summary says so. Gone / forbidden / not connected
 * render nothing here (SessionContext says it once).
 */
export default function DiagnosisStats({ context }) {
  const { status, data } = context;
  if (status === CONTEXT_STATUS.NONE || status === CONTEXT_STATUS.GONE || status === CONTEXT_STATUS.FORBIDDEN
    || status === CONTEXT_STATUS.NOT_CONNECTED || status === CONTEXT_STATUS.FAILED) return null;
  const loading = status === CONTEXT_STATUS.LOADING;
  const diagnosis = data && data.diagnosis;
  return (
    <div className="le-fields" role="group" aria-label={S.heading} aria-busy={loading}>
      <div className="le-field">
        <RoughHead label={S.heading} />
        {loading && <span className="sr-only" role="status">{S.loading}</span>}
        {loading && <SkeletonGrid />}
        {!loading && !diagnosis && <p className="le-muted">{S.none}</p>}
        {!loading && diagnosis && (
          <>
            <StatGrid tiles={diagnosisTiles(diagnosis)} label={S.heading} />
            {diagnosis.pinned && <p className="le-note">{S.pinned}</p>}
          </>
        )}
      </div>
      {!loading && diagnosis && (
        <div className="le-field">
          <RoughHead label={S.causes.heading} hint={S.causes.how} />
          <Shares diagnosis={diagnosis} />
          <RuledOut diagnosis={diagnosis} />
        </div>
      )}
      {!loading && diagnosis && diagnosis.fix && (
        <div className="le-field">
          <span className="le-label">{S.fix}</span>
          <p className="le-text">{diagnosis.fix}</p>
        </div>
      )}
      {(loading || diagnosis) && <DiagnosisTimeline steps={timelineSteps(diagnosis)} total={diagnosis ? diagnosis.steps : 0} loading={loading} />}
    </div>
  );
}
